import { GitHubIcon } from "@/components/GitHubIcon";
import { GoogleIcon } from "@/components/GoogleIcon";
import { MailIcon } from "@/components/MailIcon";
import { WardMark } from "@/components/WardMark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";

const ERROR_FALLBACKS: Record<string, string> = {
  account_too_new: "GitHub accounts must be at least 30 days old to sign up.",
  disposable_email: "Disposable email addresses are not accepted.",
  bad_state: "Login session expired or invalid. Try again.",
  bad_token: "That sign-in link expired or has already been used. Request a new one.",
  provider_error: "Sign-in was cancelled or rejected by the provider.",
  missing_params: "Sign-in didn't complete. Try again.",
  token_exchange_failed: "Could not complete sign-in.",
  profile_fetch_failed: "Could not load your profile from the provider.",
};

function emailErrorMessage(code: string | undefined): string {
  switch (code) {
    case "bad_email":
      return "Enter a valid email address.";
    case "disposable_email":
      return "Disposable email addresses are not accepted.";
    case "ip_rate_limited":
      return "Too many sign-in attempts from this network. Try again in a few minutes.";
    case "email_rate_limited":
      return "We just sent a link to that address. Wait a minute before requesting another.";
    default:
      return "Couldn't send the email. Try again.";
  }
}

function providerHref(provider: "github" | "google", returnTo: string | null): string {
  const base = `/bff/auth/start/${provider}`;
  return returnTo ? `${base}?return_to=${encodeURIComponent(returnTo)}` : base;
}

type EmailFormState =
  | { stage: "collapsed" }
  | { stage: "idle"; error: string | null }
  | { stage: "submitting" }
  | { stage: "sent"; email: string };

export function LoginRoute() {
  const [params] = useSearchParams();
  const returnTo = params.get("return_to");
  const error = params.get("error");
  const errorMessage = params.get("error_message");
  const message = error
    ? errorMessage || ERROR_FALLBACKS[error] || "Sign-in failed. Try again."
    : null;

  const [email, setEmail] = useState("");
  const [formState, setFormState] = useState<EmailFormState>({ stage: "collapsed" });
  const emailInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (formState.stage === "idle") emailInputRef.current?.focus();
  }, [formState.stage]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      setFormState({ stage: "idle", error: emailErrorMessage("bad_email") });
      return;
    }
    setFormState({ stage: "submitting" });
    try {
      const res = await fetch("/bff/auth/email/start", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: trimmed, return_to: returnTo ?? undefined }),
      });
      if (res.ok) {
        setFormState({ stage: "sent", email: trimmed });
        return;
      }
      const body = (await res.json().catch(() => ({}))) as { code?: string };
      const code = body.code ?? (res.status === 429 ? "ip_rate_limited" : "send_failed");
      setFormState({ stage: "idle", error: emailErrorMessage(code) });
    } catch {
      setFormState({ stage: "idle", error: emailErrorMessage("send_failed") });
    }
  }

  function reset() {
    setEmail("");
    setFormState({ stage: "idle", error: null });
  }

  return (
    <main className="auth-layout">
      <aside className="auth-story">
        <a href="https://wardkey.app" className="brand" aria-label="Wardkey home">
          <WardMark className="size-8 text-accent" />
          <span>
            wardkey<span className="text-accent">.</span>
          </span>
        </a>
        <div>
          <span className="eyebrow">A safe place for your secrets</span>
          <h2>
            Your secrets.
            <br />
            <span>In safe hands.</span>
          </h2>
          <p>
            From the first environment to your next deploy. One place to keep your team's secrets
            organized and under control.
          </p>
        </div>
        <footer>Built for the way you ship.</footer>
      </aside>
      <div className="auth-form-wrap">
        <section className="auth-form fade-up">
          <WardMark className="auth-monogram" />
          <h1>Welcome to Wardkey.</h1>
          <p className="mt-3 text-[13px] leading-relaxed text-muted-foreground">
            Sign in or create an account. Your workspace is waiting.
          </p>

          {message && (
            <div role="alert" className="mt-6 rounded-md border border-border bg-card px-3 py-2.5">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {error}
              </div>
              <div className="mt-1 text-[13px] text-foreground">{message}</div>
            </div>
          )}

          {formState.stage === "collapsed" ? (
            <>
              <div className="mt-7 flex flex-col gap-3">
                <Button asChild variant="outline" className="w-full justify-center gap-3">
                  <a href={providerHref("github", returnTo)}>
                    <GitHubIcon className="h-4 w-4" />
                    <span>Continue with GitHub</span>
                  </a>
                </Button>
                <Button asChild variant="outline" className="w-full justify-center gap-3">
                  <a href={providerHref("google", returnTo)}>
                    <GoogleIcon className="h-4 w-4" />
                    <span>Continue with Google</span>
                  </a>
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-center gap-3"
                  onClick={() => setFormState({ stage: "idle", error: null })}
                >
                  <MailIcon className="h-4 w-4" />
                  <span>Continue with email</span>
                </Button>
              </div>

              <p className="mt-6 text-center text-[11px] leading-relaxed text-muted-foreground">
                New to Wardkey? Pick any option above — we'll create your account automatically.
              </p>
            </>
          ) : (
            <div className="mt-7">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setEmail("");
                  setFormState({ stage: "collapsed" });
                }}
                className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground"
              >
                ← Other sign-in options
              </Button>

              {formState.stage === "sent" ? (
                <div
                  data-testid="magic-sent"
                  className="mt-4 rounded-md border border-border bg-card p-4"
                >
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    Check your inbox
                  </div>
                  <p className="mt-2 text-[13px] leading-relaxed text-foreground">
                    We sent a sign-in link to <span className="font-mono">{formState.email}</span>.
                    It expires in 15 minutes. Check your inbox and spam.
                  </p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={reset}
                    className="mt-3 font-mono text-[11px] uppercase tracking-widest text-muted-foreground"
                  >
                    Try a different email
                  </Button>
                </div>
              ) : (
                <form onSubmit={onSubmit} noValidate className="mt-4 flex flex-col gap-3">
                  <label
                    htmlFor="email"
                    className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground"
                  >
                    Email
                  </label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    ref={emailInputRef}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    disabled={formState.stage === "submitting"}
                  />
                  <Button type="submit" disabled={formState.stage === "submitting"}>
                    {formState.stage === "submitting" ? "Sending…" : "Send sign-in link"}
                  </Button>
                  {formState.stage === "idle" && formState.error && (
                    <div
                      role="alert"
                      data-testid="magic-error"
                      className="text-[12px] leading-relaxed text-muted-foreground"
                    >
                      {formState.error}
                    </div>
                  )}
                </form>
              )}
            </div>
          )}

          <p className="mt-4 text-center text-[11px] leading-relaxed text-muted-foreground">
            By continuing you agree to our{" "}
            <a
              href="https://wardkey.app/terms"
              className="text-foreground underline-offset-4 hover:underline"
            >
              Terms
            </a>{" "}
            and{" "}
            <a
              href="https://wardkey.app/privacy"
              className="text-foreground underline-offset-4 hover:underline"
            >
              Privacy Policy
            </a>
            .
          </p>
        </section>
      </div>
    </main>
  );
}
